import { FC } from 'react';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { ModalWrapper } from '../../../../../components/modal-wrapper/index';
import {
  accessActionCopy,
  DataRoomAccessAction,
  DataRoomAccessRow,
} from '../../../../../hooks/space/index';

export type AccessActionModalProps = {
  action: DataRoomAccessAction;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  roomName: string;
  row: DataRoomAccessRow;
};

/**
 * The confirmation a row action asks for — Revoke, Resend or Grant access — each with its own
 * title, question and button, the way every other confirmation in the Data Room is drawn.
 *
 * Mounted only while an action is awaiting an answer, so the sheet is always open: there is no
 * separate `isOpen`, because the modal going away is what closing it means.
 */
export const AccessActionModal: FC<AccessActionModalProps> = ({
  action,
  isPending,
  onCancel,
  onConfirm,
  roomName,
  row,
}) => {
  const { body, confirmLabel, isDestructive, title } = accessActionCopy({
    action,
    status: row.status,
  });

  // A response arriving after a dismissal could close whichever modal was opened since.
  const cancel = () => {
    if (!isPending) {
      onCancel();
    }
  };

  return (
    <ModalWrapper
      // Both sheets sit at z-51 and every overlay at z-50, so this one would slide under the
      // access list that opened it. Lifted over it the same way the invite drawer is — 52 for the
      // dim, 53 for the dialog — so the list reads as the layer underneath.
      className="z-[53] w-full max-w-[485px] shadow-none"
      control={{ onOpenChange: (open) => !open && cancel(), open: true }}
      onBgClick={cancel}
      overlayClassName="z-[52]"
      side="CENTER"
    >
      <div className="flex w-full flex-col gap-6 rounded-lg bg-white p-6 shadow-3xl">
        <h2 className="text-xl font-[550] text-gray-700">
          <AppFormattedMessage id={title} />
        </h2>
        <p className="break-words text-xs font-[450] leading-[18px] text-gray-500">
          <AppFormattedMessage id={body} values={{ name: row.name ?? row.email, roomName }} />
        </p>
        <div className="flex items-center justify-end gap-3">
          <Button
            className="h-11 w-fit border border-gray-300 px-6 text-sm font-[450] text-gray-700"
            disabled={isPending}
            onClick={cancel}
            styleType="DEFAULT_ROUNDED"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className={
              isDestructive
                ? 'h-11 w-fit bg-fireside-600 px-6 hover:bg-fireside-700'
                : 'h-11 w-fit px-6'
            }
            disabled={isPending}
            isLoading={isPending}
            onClick={onConfirm}
          >
            <AppFormattedMessage id={confirmLabel} />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
